import type { FC } from 'react';
import { useCallback, useMemo } from 'react';
import {
  Card,
  Grid,
  Stack,
  styled,
  Typography,
  Alert,
  AlertTitle,
  Box,
  Button,
} from '@mui/material';
import * as React from 'react';
import type {
  CreateRecipientRequest,
  RecipientStatus,
  Recipient,
  RecipientData,
} from '@pflegenavi/service-provider-frontend/api';
import {
  useGetRecipient,
  useGetServiceProviderPhoenix,
  useCreateRecipient,
  useMangopayManageProxyConsent,
} from '@pflegenavi/service-provider-frontend/api';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import {
  Label,
  LoadingPromiseButtonAdvanced,
} from '@pflegenavi/web-components';
import { BooleanStatusCard } from './BooleanStatusCard';
import { useLocale } from '@pflegenavi/frontend/localization';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import { RecipientFormWithSchema } from '../connect-account/RecipientFormWithSchema';

/**
 * API address format that can handle both snake_case and PascalCase
 */
interface ApiAddress {
  address_line1?: string;
  address_line2?: string;
  city?: string;
  region?: string;
  postal_code?: string;
  country?: string;
  AddressLine1?: string;
  AddressLine2?: string;
  City?: string;
  Region?: string;
  PostalCode?: string;
  Country?: string;
}

/**
 * Transforms recipient data from API format (snake_case) to form format (PascalCase)
 *
 * @example
 * const recipient = {
 *   display_name: 'Test Pharmacy',
 *   business_recipient: {
 *     business_name: 'Test Pharmacy',
 *     address: { address_line1: '123 Street', city: 'Paris' }
 *   }
 * };
 * const formData = transformRecipientToFormData(recipient);
 * // formData.DisplayName === 'Test Pharmacy'
 */
function transformRecipientToFormData(
  recipient: Recipient
): Partial<RecipientData> {
  const formData: Partial<RecipientData> = {
    DisplayName: recipient.display_name,
    PayoutMethodType: recipient.payout_method_type as
      | 'LocalBankTransfer'
      | 'InternationalBankTransfer',
    RecipientType: recipient.recipient_type as 'Individual' | 'Business',
    Currency: recipient.currency,
    Country: recipient.country,
  };

  // Transform business recipient data if present
  if (recipient.business_recipient) {
    const address = recipient.business_recipient.address as
      | ApiAddress
      | undefined;
    formData.BusinessRecipient = {
      BusinessName: recipient.business_recipient.business_name || '',
      Address: {
        AddressLine1: address?.address_line1 || address?.AddressLine1 || '',
        AddressLine2: address?.address_line2 || address?.AddressLine2,
        City: address?.city || address?.City || '',
        Region: address?.region || address?.Region,
        PostalCode: address?.postal_code || address?.PostalCode || '',
        Country: address?.country || address?.Country || recipient.country,
      },
    };
  }

  // Transform individual recipient data if present
  if (recipient.individual_recipient) {
    const address = recipient.individual_recipient.address as
      | ApiAddress
      | undefined;
    formData.IndividualRecipient = {
      FirstName: recipient.individual_recipient.first_name || '',
      LastName: recipient.individual_recipient.last_name || '',
      Address: {
        AddressLine1: address?.address_line1 || address?.AddressLine1 || '',
        AddressLine2: address?.address_line2 || address?.AddressLine2,
        City: address?.city || address?.City || '',
        Region: address?.region || address?.Region,
        PostalCode: address?.postal_code || address?.PostalCode || '',
        Country: address?.country || address?.Country || recipient.country,
      },
    };
  }

  // Transform local bank transfer data if present
  if (recipient.local_bank_transfer) {
    formData.LocalBankTransfer = {};
    // Transform each currency's bank details
    Object.keys(recipient.local_bank_transfer).forEach((currency) => {
      const currencyData = recipient.local_bank_transfer![currency];
      if (currencyData && formData.LocalBankTransfer) {
        formData.LocalBankTransfer[currency] = {
          IBAN: currencyData.IBAN || currencyData.iban,
          BIC: currencyData.BIC || currencyData.bic,
          AccountNumber:
            currencyData.AccountNumber || currencyData.account_number,
          SortCode: currencyData.SortCode || currencyData.sort_code,
          RoutingNumber:
            currencyData.RoutingNumber || currencyData.routing_number,
        };
      }
    });
  }

  // Transform international bank transfer data if present
  if (recipient.international_bank_transfer) {
    formData.InternationalBankTransfer = {
      AccountNumber: recipient.international_bank_transfer.account_number || '',
      BIC: recipient.international_bank_transfer.bic,
    };
  }

  return formData;
}

export const ServiceProviderProfileSettings: FC = () => {
  const { t } = useTranslation();
  const locale = useLocale();
  const { enqueueSnackbar } = useSnackbar();

  const { mutateAsync: createRecipient, isPending: isCreateRecipientLoading } =
    useCreateRecipient();

  const {
    data: serviceProviderProfile,
    isPending: isServiceProviderProfileLoading,
  } = useGetServiceProviderPhoenix();

  // Fetch recipient data from our new endpoint
  const { data: recipientData, isPending: isRecipientLoading } =
    useGetRecipient();

  const { mutateAsync: mangopayManageProxyConsent } =
    useMangopayManageProxyConsent();

  const handleSubmit = useCallback(
    async (data: CreateRecipientRequest) => {
      try {
        const result = await createRecipient(data);

        // Handle SCA redirect if present
        if (result.data.redirect_url) {
          const returnUrl = `${window.location.origin}${spAppMainPages.DASHBOARD}`;
          const separator = result.data.redirect_url.includes('?') ? '&' : '?';
          const redirectUrl = `${
            result.data.redirect_url
          }${separator}returnUrl=${encodeURIComponent(returnUrl)}`;

          enqueueSnackbar(
            t('service-provider-app.connect-account.redirecting-sca'),
            {
              variant: 'info',
            }
          );
          // Small delay to let user see the message
          setTimeout(() => {
            window.location.href = redirectUrl;
          }, 500);
        } else {
          enqueueSnackbar(
            t('service-provider-app.connect-account.recipient-created'),
            {
              variant: 'success',
            }
          );
        }
      } catch {
        enqueueSnackbar(
          t('service-provider-app.connect-account.failed-to-create-recipient'),
          {
            variant: 'error',
          }
        );
      }
    },
    [createRecipient, enqueueSnackbar, t]
  );

  const handleManageProxyConsent = useCallback(() => {
    mangopayManageProxyConsent(window.location.href)
      .then((response) => {
        if (response.redirect_url) {
          window.location.href = response.redirect_url;
        }
      })
      .catch(() => {
        enqueueSnackbar(t('profile.settings.error'), {
          variant: 'error',
        });
      });
  }, [mangopayManageProxyConsent, enqueueSnackbar, t]);

  const loading = useMemo(() => {
    return (
      isCreateRecipientLoading ||
      isServiceProviderProfileLoading ||
      isRecipientLoading
    );
  }, [
    isCreateRecipientLoading,
    isServiceProviderProfileLoading,
    isRecipientLoading,
  ]);

  const payoutScheduleLabel = useMemo(() => {
    if (!serviceProviderProfile?.data) {
      return undefined;
    }
    const { payout_schedule, mangopay_payout_day_of_week } =
      serviceProviderProfile.data;
    if (payout_schedule === 'disabled') {
      return t('sp-web-app.mangopay.payouts.disabled');
    }
    if (payout_schedule === 'daily') {
      return t('sp-web-app.mangopay.payouts.daily');
    }
    if (mangopay_payout_day_of_week != null) {
      // Note that January 1st is a Monday in 2024, which is why we use that year specifically
      return new Intl.DateTimeFormat(locale, { weekday: 'long' }).format(
        new Date(2024, 0, mangopay_payout_day_of_week)
      );
    }
    return undefined;
  }, [locale, serviceProviderProfile, t]);

  // Get the active recipient if any
  const activeRecipient = useMemo(() => {
    // First check if we have recipient data from our new endpoint
    if (recipientData?.data?.recipient) {
      return recipientData.data.recipient;
    }
    return null;
  }, [recipientData]);

  // Check if we can resubmit a cancelled recipient
  const canResubmitRecipient = useMemo(() => {
    const status =
      recipientData?.data?.service_provider?.mangopay_recipient_status;
    return status === 'CANCELED' || status === 'DEACTIVATED';
  }, [recipientData]);

  // Check if recipient exists and is in a final or pending state
  const hasExistingRecipient = useMemo(() => {
    const status =
      recipientData?.data?.service_provider?.mangopay_recipient_status;
    return Boolean(status && (status === 'ACTIVE' || status === 'PENDING'));
  }, [recipientData]);

  // Get recipient type based on legal person type
  const recipientType = useMemo(() => {
    return serviceProviderProfile?.data?.legal_person_type === 'SOLETRADER'
      ? 'Individual'
      : 'Business';
  }, [serviceProviderProfile]);

  // Transform recipient data to form format
  const recipientFormData = useMemo(() => {
    if (activeRecipient) {
      return transformRecipientToFormData(activeRecipient);
    }
    return undefined;
  }, [activeRecipient]);

  // Helper to get recipient status color
  const getRecipientStatusColor = (status: RecipientStatus) => {
    switch (status) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELED':
      case 'DEACTIVATED':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Grid container spacing={3}>
      <Grid
        size={{
          xs: 12,
          lg: 8,
        }}
      >
        <ServiceProviderProfileSettingsForm
          recipientType={recipientType}
          handleSubmit={handleSubmit}
          loading={loading}
          hasBankAccount={Boolean(
            serviceProviderProfile?.data.mangopay_bank_account
          )}
          canResubmitRecipient={canResubmitRecipient}
          hasExistingRecipient={hasExistingRecipient}
          initialValues={recipientFormData}
        />
      </Grid>
      <Grid
        size={{
          xs: 12,
          lg: 4,
        }}
      >
        <Stack direction="column" gap={3}>
          <BooleanStatusCard
            activeLabel={t('sp-web-app.mangopay.payouts.payouts')}
            activeValue={
              payoutScheduleLabel ?? t('sp-web-app.mangopay.payouts.active')
            }
            inactiveLabel={t('sp-web-app.mangopay.payouts.payouts')}
            inactiveValue={t('sp-web-app.mangopay.payouts.inactive')}
            active={
              Boolean(
                activeRecipient ||
                  serviceProviderProfile?.data.mangopay_bank_account
              ) && Boolean(serviceProviderProfile?.data.mangopay_kyc_verified)
            }
          />

          {/* Show recipient status if using new recipient system */}
          {recipientData?.data?.service_provider?.mangopay_recipient_status && (
            <Card sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography fontWeight={600}>
                  {t('sp-web-app.mangopay.recipients.status')}
                </Typography>
                {/* Show status from new endpoint if available */}
                {recipientData?.data?.service_provider
                  ?.mangopay_recipient_status && (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography>
                      {t('sp-web-app.mangopay.recipients.status-label')}:
                    </Typography>
                    <Label
                      variant="filled"
                      color={getRecipientStatusColor(
                        recipientData.data.service_provider
                          .mangopay_recipient_status as RecipientStatus
                      )}
                      sx={{ color: 'white' }}
                    >
                      {t(
                        `sp-web-app.mangopay.recipients.status-${recipientData.data.service_provider.mangopay_recipient_status.toLowerCase()}`
                      )}
                    </Label>
                  </Stack>
                )}
                {recipientData.data.service_provider.iban_last_4 && (
                  <Typography variant="subtitle2">
                    IBAN: ****&nbsp;
                    {recipientData.data.service_provider.iban_last_4}
                  </Typography>
                )}
              </Stack>
            </Card>
          )}

          {/* Manage Proxy Consent button */}
          {serviceProviderProfile?.data?.mangopay_sca_enrolled && (
            <Card sx={{ p: 2 }}>
              <Stack spacing={2}>
                <Typography fontWeight={600}>
                  {t('profile.settings.manage-proxy-consent-title')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('profile.settings.manage-proxy-consent-description')}
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleManageProxyConsent}
                >
                  {t('profile.settings.manage-proxy-consent')}
                </Button>
              </Stack>
            </Card>
          )}
        </Stack>
      </Grid>
    </Grid>
  );
};

export interface ServiceProviderProfileSettingsFormProps {
  recipientType: 'Individual' | 'Business';
  handleSubmit: (data: CreateRecipientRequest) => Promise<void>;
  loading?: boolean;
  hasBankAccount?: boolean;
  canResubmitRecipient?: boolean;
  hasExistingRecipient?: boolean;
  initialValues?: Partial<RecipientData>;
}

export const ServiceProviderProfileSettingsForm: FC<
  ServiceProviderProfileSettingsFormProps
> = ({
  recipientType,
  handleSubmit,
  loading,
  hasBankAccount,
  canResubmitRecipient,
  hasExistingRecipient,
  initialValues,
}) => {
  const { t } = useTranslation();
  const [isFormValid, setIsFormValid] = React.useState(false);

  const disabled = useMemo(() => {
    // Disable if loading
    if (loading) {
      return true;
    }

    // Disable if recipient is active or pending (not editable)
    if (hasExistingRecipient) {
      return true;
    }

    // Disable if has bank account and cannot resubmit (legacy check)
    if (hasBankAccount && !canResubmitRecipient) {
      return true;
    }

    return false;
  }, [loading, hasExistingRecipient, hasBankAccount, canResubmitRecipient]);

  return (
    <ContainerCard>
      <Stack gap={2}>
        {canResubmitRecipient && (
          <Alert severity="warning">
            <AlertTitle>
              {t('sp-web-app.mangopay.recipients.canceled.title')}
            </AlertTitle>
            {t('sp-web-app.mangopay.recipients.canceled.message')}
          </Alert>
        )}

        <Box>
          <RecipientFormWithSchema
            recipientType={recipientType}
            onSubmit={handleSubmit}
            disabled={disabled}
            canResubmit={canResubmitRecipient}
            currency="EUR"
            onValidationChange={setIsFormValid}
            initialValues={initialValues}
          />

          <Stack direction="row" justifyContent="end" gap={1} mt={2}>
            <LoadingPromiseButtonAdvanced
              variant="contained"
              size="large"
              disabled={!isFormValid || disabled}
              onClick={() => {
                // Submit is handled by the RecipientFormWithSchema
                const formElement = document.querySelector('form');
                if (formElement) {
                  formElement.dispatchEvent(
                    new Event('submit', { bubbles: true, cancelable: true })
                  );
                }
              }}
            >
              {hasExistingRecipient
                ? t('sp-web-app.mangopay.recipients.already-connected')
                : canResubmitRecipient
                ? t('sp-web-app.mangopay.recipients.resubmit')
                : hasBankAccount
                ? t('sp-web-app.mangopay.recipients.already-connected')
                : t('service-provider-app.connect-account.save')}
            </LoadingPromiseButtonAdvanced>
          </Stack>
        </Box>
      </Stack>
    </ContainerCard>
  );
};

const ContainerCard = styled(Card)(({ theme }) =>
  theme.unstable_sx({
    flex: 1,
    p: 3,
  })
);
